/** After setup: the founder's computer, their app, their people, and the way back to pairing and sign-in. Calm. */
import { useState } from 'react'
import { Link, useNavigate } from 'react-router'
import { addressOf } from '../../domain/copy'
import { healthRows, heardFrom } from '../../domain/health'
import { lineOk } from '../../domain/machine'
import { Check, Out } from '../components/icons'
import { TeamPanel } from '../components/People'
import { Dialog, Empty, Section, StatusChip } from '../components/ui'
import { useLoad } from '../useLoad'
import { useFounder } from './context'
import { LearnCard } from './LearnCard'
import { UsageCard } from './UsageCard'
import { PreviewNote } from './Machine'
import { visibleSteps } from '../../domain/setup'
import { GetPaseo, PairPanel, ShopifyConnected } from './panels'

export function Dashboard() {
  const f = useFounder()
  const w = f.state.world
  const host = addressOf(w)
  const conns = useLoad(() => f.api.connections(), [f.api])
  const nav = useNavigate()
  const [dialog, setDialog] = useState<'pair' | null>(null)
  const agent = f.personSetup.agent
  const signedIn = agent === 'codex' ? f.codex?.loggedIn : f.claude?.loggedIn
  const devices = f.state.world.devices ?? f.personSetup.devices
  /** "Run setup again": the marks go and setup walks as a tutorial; nothing on the computer is undone. */
  const runAgain = () => {
    void f.rerun().catch(() => {})
    nav(`/setup/${visibleSteps(f.setup)[0]}`)
  }
  const heard = heardFrom(w.box?.lastHeartbeatAt ?? f.state.heartbeat?.at ?? null, f.now)
  const shopify = conns.data?.find((c) => c.key === 'shopify')

  return (
    <>
      <div className="page-head">
        <div>
          <span className="crumb">Your computer</span>
          <h1>{w.founder.name}</h1>
        </div>
        <button className="btn btn-ghost btn-sm" type="button" onClick={runAgain}>
          Run setup again
        </button>
      </div>

      <div className="dash">
        <Section title="This week">
          <UsageCard api={f.api} />
        </Section>

        <Section title="Health" className="d-health" action={<StatusChip status={heard.quiet ? 'manual' : f.state.heartbeat ? 'active' : 'pending'} label={heard.words} />}>
          {f.state.heartbeat ? (
            <dl className="health">
              {healthRows(f.state.heartbeat.health).map((r) => (
                <div key={r.label} className={r.attention ? 'attn' : undefined}>
                  <dt>{r.label}</dt>
                  <dd>
                    <span>{r.words}</span>
                    {r.value && <span className="mono muted">{r.value}</span>}
                  </dd>
                </div>
              ))}
            </dl>
          ) : (
            <Empty>Waiting for your computer's next report. It checks in every 30 seconds.</Empty>
          )}
        </Section>

        <Section title="Your app" className="d-app">
          <dl className="facts">
            <div>
              <dt>Address</dt>
              <dd>
                {host && lineOk(f.lines, 'address') ? (
                  <a className="url-chip" href={`https://${host}`} target="_blank" rel="noreferrer">
                    {host} <Out />
                  </a>
                ) : (
                  <span className="muted">{host ? `${host}, live soon` : 'Not set up yet'}</span>
                )}
              </dd>
            </div>
            <div>
              <dt>Preview</dt>
              <dd>
                <PreviewNote empty="Shows here once your computer is ready." />
              </dd>
            </div>
            <div>
              <dt>Code</dt>
              <dd>{w.app?.repo ? <span className="mono">{w.app.repo}</span> : <span className="muted">Not made yet</span>}</dd>
            </div>
          </dl>
        </Section>

        <Section title="Checks" className="d-checks" count={f.lines.length}>
          <ul className="checklist">
            {f.lines.map((l) => (
              <li key={l.key} className={`c-${l.status}`}>
                <span className="c-tk">{l.status === 'ok' && <Check size={11} />}</span>
                <span>{l.label}</span>
                <span className="label">{l.status === 'ok' ? 'ok' : l.status === 'failing' ? "we're on it" : l.status === 'running' ? 'now' : 'waiting'}</span>
              </li>
            ))}
          </ul>
        </Section>

        <Section title="Devices and sign-in" className="d-devices">
          <div className="stack-sm">
            <div className="d-row">
              <span>
                <b>Paseo</b>
                <span className="aside">{devices ? (devices.seen ? `${devices.seen} device${devices.seen === 1 ? '' : 's'} paired. Pair as many as you like.` : 'No device paired yet.') : 'Pair a phone, iPad or laptop to build from.'}</span>
              </span>
              <button className="btn btn-soft btn-sm" type="button" onClick={() => setDialog('pair')} disabled={!lineOk(f.lines, 'phone')}>
                Pair another device
              </button>
            </div>
            <div className="d-row">
              <span>
                <b>{agent === 'codex' ? 'Codex' : 'Claude'}</b>
                <span className="aside">{signedIn ? `Your coding agent, signed in${agent === 'claude' && f.claude?.email ? ` as ${f.claude.email}` : ''}` : 'Your coding agent, not signed in on your computer yet'}</span>
              </span>
              <button className="btn btn-soft btn-sm" type="button" onClick={() => nav('/setup/agent')}>
                {signedIn ? 'Switch or sign in again' : 'Sign in'}
              </button>
            </div>
          </div>
        </Section>

        <Section title="People" className="d-people">
          <TeamPanel api={f.api} me={f.me.person.id} reader={f.me.person.role === 'founder' ? 'founder' : 'teammate'} />
        </Section>

        <Section title="Connections" className="d-conns" action={<Link to="/connections" className="btn btn-ghost btn-sm">Manage</Link>}>
          {shopify?.status === 'connected' ? (
            <ShopifyConnected name={shopify.detail ?? 'your store'} warnings={shopify.warnings} />
          ) : (
            <p className="aside">
              Shopify isn't connected yet. <Link to="/connections">Connect it</Link> so your app reads your real data.
            </p>
          )}
        </Section>

        <Section title="Learn" className="d-learn">
          <LearnCard api={f.api} state={{ previewReady: Boolean(w.preview?.url), shopifyConnected: conns.data ? shopify?.status === 'connected' : undefined }} />
        </Section>
      </div>

      <Dialog open={dialog === 'pair'} onClose={() => setDialog(null)} title="Pair another device">
        <p className="aside">Get Paseo on the new device first.</p>
        <GetPaseo />
        <PairPanel />
        <div className="form-foot">
          <button className="btn btn-ghost" type="button" onClick={() => setDialog(null)}>
            Done
          </button>
        </div>
      </Dialog>
    </>
  )
}
